/**
 * Empty → null normalization for the clearable (nullable) profile fields (#2931).
 *
 * `tagline`, `location`, `primary_sport` and `secondary_sports` are all
 * `.nullable().optional()` on `UpdateProfileSchema`, and the `PUT /profile`
 * handler clears the underlying column when it receives an explicit `null`.
 *
 * The clients used to emit `undefined` for an emptied field
 * (`value || undefined`). `JSON.stringify` DROPS `undefined` keys entirely, so
 * the field never reached the API, `if (body.field !== undefined)` was false,
 * and the column was never cleared — the user watched their deletion undo
 * itself on reload. That is the #776 regression: its comment said "null", its
 * code shipped "undefined", the one sentinel that cannot clear the column.
 *
 * `null` is the only sentinel that BOTH survives serialization AND reaches the
 * `.nullable()` schema branch. This function is the single source of truth for
 * that mapping so the web page and the native screen can never drift apart
 * again (a duplicated `|| undefined` is exactly how the bug landed in both).
 *
 * Pure and import-free by design: it is imported at RUNTIME by both the web
 * page (`lib/api/profileFields`) and the native screen
 * (`@shared/lib/api/profileFields`, Metro-resolved like `@shared/lib/phone`),
 * so it must not pull in any web-only module.
 */

export interface ClearableProfileFieldInput {
  tagline?: string | null;
  location?: string | null;
  primary_sport?: string | null;
  secondary_sports?: string[] | null;
}

export interface ClearableProfileFields {
  tagline: string | null;
  location: string | null;
  primary_sport: string | null;
  secondary_sports: string[] | null;
}

/**
 * Map each clearable profile field to its wire value: a trimmed non-empty
 * string / non-empty array passes through, anything empty (or whitespace-only)
 * becomes `null` so the API clears the column.
 */
export function normalizeClearableProfileFields(
  input: ClearableProfileFieldInput,
): ClearableProfileFields {
  const tagline = input.tagline?.trim();
  const location = input.location?.trim();
  const primarySport = input.primary_sport?.trim();

  return {
    tagline: tagline ? tagline : null,
    location: location ? location : null,
    primary_sport: primarySport ? primarySport : null,
    secondary_sports:
      input.secondary_sports && input.secondary_sports.length > 0
        ? input.secondary_sports
        : null,
  };
}
